import type { Microsoft365Source } from '@dbrain/shared';
import { Icons, Menu } from '@dbrain/ui';
import { useId, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { SourceTile } from './icons.js';
import type { AddMenuView } from './model.js';

/**
 * The composer's "+" (MsEkleMenu), the same on the home page and in a conversation: "Bilgisayardan yükle", then in
 * Chat and Cowork the MICROSOFT 365 group with the connected address in its header (OneDrive, SharePoint, Outlook
 * ekleri, each only while its switch is on; addMenuView), then the page's own items (Skill kullan, Bağlantı ekle).
 * "Belgelerimden ekle" is not in this release. Every row is two lines: what it does and where from.
 */
export function AddMenu({
  label,
  direction,
  experience,
  maxUploadMb,
  onUpload,
  uploadDisabled,
  microsoft,
  onMicrosoft,
  children,
  testId,
  uploadTestId,
}: {
  label: string;
  direction?: 'up' | 'down';
  experience: 'chat' | 'code';
  maxUploadMb: number;
  onUpload: () => void;
  uploadDisabled?: boolean;
  /** the Microsoft 365 group; null: not shown */
  microsoft: AddMenuView | null;
  onMicrosoft: (source: Microsoft365Source) => void;
  /** the page's own rows after the line (AddMenuRow) */
  children?: ReactNode;
  testId: string;
  uploadTestId: string;
}) {
  const { t } = useTranslation();
  const groupId = useId();
  return (
    <Menu
      className="composer-box__add"
      label={label}
      triggerClassName="ui-icon-button"
      trigger={<Icons.plus size={20} />}
      direction={direction}
      panelClassName="m365-add"
      data-testid={testId}
    >
      <AddMenuRow
        icon={<Icons.upload size={17} />}
        title={t('microsoft365.files.menu.computer')}
        hint={t(experience === 'code' ? 'microsoft365.files.menu.computerHintCode' : 'microsoft365.files.menu.computerHint', { mb: maxUploadMb })}
        onClick={onUpload}
        disabled={uploadDisabled}
        testId={uploadTestId}
      />
      {microsoft ? (
        <div className="m365-add__group" role="group" aria-labelledby={groupId} data-testid="m365-add-group">
          <p className="m365-add__head" id={groupId}>
            {/* an English name: upper case without the Turkish dotted İ */}
            <span className="m365-add__head-name" lang="en">
              {t('microsoft365.files.menu.group')}
            </span>
            <span className="m365-add__head-line" aria-hidden="true" />
            {microsoft.email ? (
              <span className="m365-add__email" data-testid="m365-add-email">
                {microsoft.email}
              </span>
            ) : null}
          </p>
          {microsoft.sources.map((source) => (
            <AddMenuRow
              key={source}
              icon={<SourceTile source={source} size="lg" />}
              tiled={false}
              title={t(`microsoft365.files.sources.${source}`)}
              hint={t(`microsoft365.files.menu.hints.${source}`)}
              chevron
              onClick={() => onMicrosoft(source)}
              testId={`m365-add-${source}`}
            />
          ))}
        </div>
      ) : null}
      {children ? <div className="m365-add__rest">{children}</div> : null}
    </Menu>
  );
}

/** One row of the "+" menu: an icon on a tile, the name, a line under it; a button or a link inside the app. */
export function AddMenuRow({
  icon,
  tiled = true,
  title,
  hint,
  chevron = false,
  to,
  onClick,
  disabled,
  testId,
}: {
  icon: ReactNode;
  /** false: the icon brings its own tile */
  tiled?: boolean;
  title: string;
  hint?: string;
  chevron?: boolean;
  /** a page of this app */
  to?: string;
  onClick?: () => void;
  disabled?: boolean;
  testId?: string;
}) {
  const body = (
    <>
      {tiled ? (
        <span className="m365-add__tile" aria-hidden="true">
          {icon}
        </span>
      ) : (
        icon
      )}
      <span className="m365-add__text">
        <span className="m365-add__title">{title}</span>
        {hint ? <span className="m365-add__hint">{hint}</span> : null}
      </span>
      {chevron ? <Icons.chevronRight size={15} className="m365-add__chevron" /> : null}
    </>
  );
  if (to) {
    return (
      <Link className="ui-menu-item m365-add__item" to={to} data-testid={testId}>
        {body}
      </Link>
    );
  }
  return (
    <button type="button" className="ui-menu-item m365-add__item" disabled={disabled} onClick={onClick} data-testid={testId}>
      {body}
    </button>
  );
}
